import { useQuery } from "@tanstack/react-query";
import { z } from "zod";

import { api } from "~/shared/services/api/fetch-client.ts";

// Define the load values schema
const LoadValueSchema = z.object({
	code: z.string(),
	displayName: z.string(),
});

const LoadValuesResponseSchema = z.array(LoadValueSchema);

export type LoadValue = z.infer<typeof LoadValueSchema>;
export type LoadValuesResponse = z.infer<typeof LoadValuesResponseSchema>;

const loadValuesQueryKey = "load-values";

// API function to fetch load values
async function fetchLoadValues(): Promise<LoadValuesResponse> {
	const response = await api.get("loadvalues");

	if (!response.ok) {
		throw new Error(`Failed to fetch load values: ${response.statusText}`);
	}

	const data = await response.json();

	// Validate response with Zod schema
	return LoadValuesResponseSchema.parse(data);
}

// Hook to load values with TanStack Query
export function useLoadValues() {
	const { data, error, isLoading } = useQuery({
		queryKey: [loadValuesQueryKey],
		queryFn: fetchLoadValues,
		staleTime: 5 * 60 * 1000, // 5 minutes
		gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
		retry: 2,
	});

	return {
		loadValues: data,
		isLoading,
		error,
	};
}
